import { sessionFetcher } from 'components/authInitializer';
import Button from 'components/Button';
import { TextInput } from 'components/Forms/lib';
import SelectInput, { OptionsProps } from 'components/Forms/lib/SelectInput';
import ToggleInput from 'components/Forms/lib/ToggleInput';
import BACKEND_URL from 'constants/backendUrl';
import { REGEXP } from 'constants/validationsForm';
import { Civility } from 'core/enums/civility';
import User from 'core/models/user';
import UserProfile from 'core/models/userProfile';
import useResponsive from 'hooks/responsive';
import { useIsPremium } from 'hooks/userIsPremium';
import { omit } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSessionStore } from 'stores/session';
import { SSpacedRowWrapped } from 'styles/layout';
import { enumKeys } from 'utils/common';
import { PUT } from 'utils/http';
import { SInputWrapper } from '../styles';

type Inputs = Omit<User, 'id' | 'role'>;

type UpdateUserInformationRequest = {
    user: User;
};

const updateUserInformationRequest = async (inputs: Inputs, id: string): Promise<User> => {
    const response = (await PUT<UpdateUserInformationRequest>(`${BACKEND_URL}/api/users/${id}`, {
        ...inputs,
        id,
    })) as UpdateUserInformationRequest;

    return response.user;
};

// Reprend la transformation exacte de l'ancien userProfileSelector.
const toUserProfile = (user: User | null): UserProfile | null => {
    if (!user) {
        return null;
    }

    return {
        civility: user.civility,
        createdDate: user.createdDate,
        email: user.email,
        firstLoginDate: user.firstLoginDate,
        firstName: user.firstName,
        hasPaid: user.hasPaid,
        job: user.job,
        lastLoginDate: user.lastLoginDate,
        lastName: user.lastName,
        mobileNumber: user.mobileNumber,
        phoneNumber: user.phoneNumber,
        status: user.status,
        taskNotifications: user.taskNotifications,
        trialEndDate: user.trialEndDate,
    };
};

const UserInformationForm: FC = () => {
    const { t } = useTranslation();
    const user = useSessionStore((s) => s.user);
    const setUser = useSessionStore((s) => s.setUser);
    const defaultValues = toUserProfile(user);
    const id = user?.id ?? null;
    const [civilityOptions, setCivilityOptions] = useState<OptionsProps>([]);
    const { isMobile } = useResponsive();
    const isPremium = useIsPremium();
    const history = useHistory();

    const methods = useForm<Inputs>({
        // Remove from default values licences if candidate / agency if agency admin
        defaultValues: defaultValues ?? undefined,
    });
    const { handleSubmit, control } = methods;

    const onSubmit = handleSubmit(async (values: Inputs) => {
        try {
            if (!id) return;
            await updateUserInformationRequest(omit(values, ['hasPaid', 'status']), id);
            const updatedUser = await sessionFetcher();
            setUser(updatedUser);
            toast.success(t('profile.update.success'));
        } catch (e) {
            toast.error(t('profile.update.error'));
        }
    });

    useEffect(() => {
        const civilityOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(Civility)) {
            civilityOptionsComputed.push({
                label: t(`common.civility.enum.${Civility[value]}`),
                value: Civility[value],
            });
        }
        setCivilityOptions(civilityOptionsComputed);
    }, [setCivilityOptions, t]);

    if (!defaultValues || !id) {
        return null;
    }

    const onClickToPremiumField = () => {
        if (isPremium === false) {
            history.push('/ask-subscription');
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <SSpacedRowWrapped>
                    <SInputWrapper>
                        <SelectInput
                            control={control}
                            label={t('common.civility.label')}
                            name="civility"
                            options={civilityOptions}
                            placeholder={t('common.civility.placeholder')}
                        />
                    </SInputWrapper>
                    <SInputWrapper>
                        <TextInput
                            label={t('common.first-name.label')}
                            name="firstName"
                            placeholder={t('common.first-name.placeholder')}
                        />
                    </SInputWrapper>
                    <SInputWrapper>
                        <TextInput
                            label={t('common.last-name.label')}
                            name="lastName"
                            placeholder={t('common.last-name.placeholder')}
                        />
                    </SInputWrapper>
                    <SInputWrapper>
                        <TextInput
                            label={t('common.mobile-number.label')}
                            name="mobileNumber"
                            pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                            placeholder={t('common.mobile-number.placeholder')}
                        />
                    </SInputWrapper>
                    <SInputWrapper>
                        <TextInput
                            label={t('common.phone-number.label')}
                            name="phoneNumber"
                            pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                            placeholder={t('common.phone-number.placeholder')}
                        />
                    </SInputWrapper>
                    <SInputWrapper>
                        <TextInput
                            label={t('common.email.label')}
                            name="email"
                            pattern={{ message: t(REGEXP.EMAIL.message), value: REGEXP.EMAIL.value }}
                            placeholder={t('common.email.placeholder')}
                            type="email"
                            disabled
                        />
                    </SInputWrapper>
                    <SInputWrapper>
                        <TextInput label={t('common.job.label')} name="job" placeholder={t('common.job.placeholder')} />
                    </SInputWrapper>
                </SSpacedRowWrapped>
                <SSpacedRowWrapped>
                    <SInputWrapper onClick={onClickToPremiumField}>
                        <ToggleInput
                            control={control}
                            disabled={!isPremium}
                            label={t('common.notifications.task-notification-label')}
                            name="taskNotifications"
                            isPremiumFeature
                        />
                    </SInputWrapper>
                </SSpacedRowWrapped>
                <Button large={isMobile} submit>
                    {t('common.save')}
                </Button>
            </form>
        </FormProvider>
    );
};

export default UserInformationForm;
